import React, { useContext } from 'react';

import { AppContext } from '@prisjakt/ui-app-context';

import ChevonRight from 'mdi-react/ChevronRightIcon';
import Clickable from '@prisjakt/ui-clickable';
import {
  TitleMediumText,
  BodySmallText,
  CaptionText,
  FontEmphasis,
  FontColor,
} from '@prisjakt/ui/text/variants';
import Currency from '@prisjakt/ui-currency';
import { CompareCheckbox } from '../compare-checkbox';
import { DealBadge } from '../deal-badge';
import { ProductMedia } from '../media';
import { Price } from '../price';

import {
  Card,
  CardActionArea,
  Main,
  ImageWrapper,
  MainContent,
  RowWrapper,
  PriceContent,
  PriceTextWrapper,
  RatingDesktop,
  RatingMobile,
  BrandLogo,
  Tag,
  CTAText,
} from './styles';

import { Product } from '../../../../../src/types';
import { BaseProps } from '../../types';

type Props = Omit<BaseProps, 'category'> & {
  priceRow?: Product['cheapestPrice'];
};

export const ProductListCard = (props: Props) => {
  const { t } = useContext(AppContext);

  const {
    actionButton,
    brand,
    className,
    container,
    diffPercentage = 0,
    highlightedName,
    imageSrc,
    imageSize = 100,
    lazyLoadImage = false,
    id,
    name,
    link,
    onClick,
    properties,
    price,
    priceCount,
    priceInclShipping,
    rating,
    reviewsCount,
    rank,
    selectable,
    selected = false,
    onDeselect = () => {},
    onSelect = () => {},
  } = props;

  const prettyProperties = properties?.length
    ? properties
        .filter(p => p.isCore)
        .map(p => p.pretty)
        .filter(x => x)
    : [];

  const handleSelect = (productId?: number) => {
    if (!productId) {
      return;
    }

    if (selected) {
      onDeselect();
    } else {
      onSelect();
    }
  };

  const handleOnClick = () => {
    if (onClick && id) onClick(id);
  };

  return (
    <Card
      selected={selected}
      className={className}
      container={container}
      containerProps={{
        'data-test': 'ProductListCard',
      }}
    >
      {!selectable && actionButton ? actionButton : null}

      <CardActionArea
        container={Clickable}
        containerProps={{
          to: selectable ? undefined : link,
          onClick: selectable ? () => handleSelect(id) : handleOnClick,
        }}
      >
        <Main>
          {selectable ? <CompareCheckbox selected={selected} /> : null}

          <ImageWrapper $size={`${imageSize}px`}>
            {brand?.logo && <BrandLogo src={brand.logo} alt={brand.name} />}

            <ProductMedia
              imageSrc={imageSrc}
              imageSize={imageSize}
              name={name || ''}
              lazyLoadImage={lazyLoadImage}
            />
          </ImageWrapper>

          <MainContent>
            <TitleMediumText containerProps={{ 'data-test': 'ProductName' }}>
              {highlightedName || name}
            </TitleMediumText>

            <BodySmallText emphasis={FontEmphasis.MEDIUM}>
              {prettyProperties.join(', ')}
            </BodySmallText>

            <RowWrapper>
              {rank && rank > 0 && (
                <Tag>
                  <CaptionText emphasis={FontEmphasis.MEDIUM}>
                    {t('product', 'rank.position', {
                      rank,
                    })}
                  </CaptionText>
                </Tag>
              )}

              {rating ? (
                <>
                  <RatingDesktop
                    rate={rating}
                    count={reviewsCount}
                    showCounter
                    showCounterAs="number"
                    showRate
                    showRateAs="number"
                  />
                  <RatingMobile
                    rate={rating}
                    count={reviewsCount}
                    showCounter
                    showCounterAs="number"
                    showRate
                    showRateAs="number"
                    singleStar
                  />
                </>
              ) : null}
            </RowWrapper>

            <RowWrapper>
              {diffPercentage ? (
                <DealBadge
                  diffPercentage={diffPercentage}
                  position="relative"
                />
              ) : null}

              {price && (
                <PriceTextWrapper gap={0.5}>
                  <BodySmallText emphasis={FontEmphasis.LOW}>
                    {t('product', 'price.from-abbr')}
                  </BodySmallText>{' '}
                  <Price price={price} isDeal={Boolean(diffPercentage)} />
                  {priceInclShipping ? (
                    <BodySmallText emphasis={FontEmphasis.LOW}>
                      {t('product', 'price.including-shipping', {
                        price: <Currency price={priceInclShipping} />,
                      })}
                    </BodySmallText>
                  ) : null}
                </PriceTextWrapper>
              )}
            </RowWrapper>
          </MainContent>

          <PriceContent>
            {typeof priceCount === 'number' && (
              <CTAText>
                <BodySmallText color={FontColor.PRIMARY}>
                  {t('category', 'product-offer.cta', { n: priceCount })}
                </BodySmallText>
                <ChevonRight />
              </CTAText>
            )}
          </PriceContent>
        </Main>
      </CardActionArea>
    </Card>
  );
};
